/**
 * The page half of the update flow (T890 — `pwa-contract.md` §4), mounted once from
 * `app/root.tsx`, which lives as long as the document — the player included, where
 * `PwaProvider` stands down.
 *
 * 1. Registers the service worker, then asks for a new one on every client navigation: the
 *    browser itself only looks on a document navigation (the T890 diagnosis). The first
 *    path is skipped because the document load that rendered it just looked.
 * 2. Turns the next navigation the app starts — her click, or one of its own redirects (the
 *    reset-password countdown) — into a document load of the same page once a new version
 *    waits. It sits in React Router's blocker, not in a click listener, so it only
 *    ever sees a navigation the page really started: a link whose own handler cancels the
 *    navigation to ask a question (the auto-test's «Salir del test») keeps its question —
 *    the first version decided on the click and skipped it (infra audit round 1, defect 1).
 *    Update switching never blocks Back and Forward: loading them as a new document would
 *    add a history entry where she meant to move through the ones she has. A route's leave
 *    confirmation may hold them; accepting it then preserves the original history action.
 *
 * React Router allows one active blocker per router. Routes register their leave guard
 * below: its confirmation comes before an update switch, without a competing blocker.
 */
import { useEffect, useRef } from "react";
import { createPath, useBlocker, useLocation } from "react-router";
import { registerServiceWorker } from "~/lib/pwa/register-sw";

export type NavigationLeaveControls = { proceed: () => void; reset: () => void };
/** `keeps` answers true for a destination the route carries across itself (T962: the same
 * practice in another language) — that navigation is neither asked about nor turned into a
 * document load, because either would lose what the route is keeping. */
type LeaveGuard = { enabled: () => boolean; blocked: (controls: NavigationLeaveControls) => void; keeps?: (path: string) => boolean };
// Registrations exist only between client effects; never populated during SSR.
const leaveGuards = new Set<LeaveGuard>();

export function useNavigationLeaveGuard(enabled: boolean, onBlocked: LeaveGuard["blocked"], keeps?: LeaveGuard["keeps"]): void {
  const latest = useRef({ enabled, onBlocked, keeps });
  latest.current = { enabled, onBlocked, keeps };
  useEffect(() => {
    const guard: LeaveGuard = { enabled: () => latest.current.enabled, blocked: controls => latest.current.onBlocked(controls), keeps: path => latest.current.keeps?.(path) ?? false };
    leaveGuards.add(guard);
    return () => { leaveGuards.delete(guard); };
  }, []);
}

export function useUpdateSwitch(): void {
  const { pathname } = useLocation();
  const firstPath = useRef(true);
  const pending = useRef<{ href: string; replace: boolean } | null>(null);
  const guarded = useRef<{ guard: LeaveGuard; path: string; action: string; notified: boolean } | null>(null);

  useEffect(() => {
    const worker = registerServiceWorker();
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    worker?.checkForUpdate();
  }, [pathname]);

  const blocker = useBlocker(({ nextLocation, historyAction }) => {
    const guard = [...leaveGuards].find(item => item.enabled());
    if (guard?.keeps?.(createPath(nextLocation))) {
      pending.current = null;
      guarded.current = null;
      return false;
    }
    if (guard) {
      pending.current = null;
      guarded.current = { guard, path: createPath(nextLocation), action: historyAction, notified: false };
      return true;
    }
    guarded.current = null;
    if (historyAction === "POP") return false;
    const href = registerServiceWorker()?.shouldSwitch(createPath(nextLocation)) ?? null;
    if (href === null) return false;
    pending.current = { href, replace: historyAction === "REPLACE" };
    return true;
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const held = guarded.current;
    if (held) {
      if (held.notified) return;
      held.notified = true;
      held.guard.blocked({
        reset: () => { guarded.current = null; blocker.reset(); },
        proceed: () => {
          guarded.current = null;
          const worker = registerServiceWorker();
          const href = held.action === "POP" ? null : worker?.shouldSwitch(held.path) ?? null;
          if (href) worker?.switchTo(href, { replace: held.action === "REPLACE" });
          else blocker.proceed();
        },
      });
      return;
    }
    if (pending.current === null) return;
    registerServiceWorker()?.switchTo(pending.current.href, { replace: pending.current.replace });
  }, [blocker]);

  // A page the switch left, restored from the back-forward cache, comes back with its blocker
  // still "blocked": blocked → blocked never re-runs the effect above, and every later link
  // would be dead (infra audit round 2, defect 1). Unblocking lets the next navigation ask
  // again; `register-sw.ts` has reset its own half on the same event.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && blocker.state === "blocked") blocker.reset();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [blocker]);
}
